import React, { useState, useEffect, useMemo } from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Search, ArrowLeft, Loader2 } from "lucide-react";
import Header from "../../layouts/Header";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useFaq,
  useFaqCategories,
  useFaqSearch,
  useFeatureFlagsByCategory,
} from "../../hooks/query/queries";
import accountIcon from "../../assets/images/faqs/account.svg";
import onboardingIcon from "../../assets/images/faqs/onboarding.svg";
import digitalWalletIcon from "../../assets/images/faqs/digitalWallet.svg";
import eightyEightPayIcon from "../../assets/images/faqs/88pay.svg";
import autostackIcon from "../../assets/images/faqs/autostack.svg";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

const TAB_ORDER = [
  { title: "Account", iconSrc: accountIcon, key: "account" },
  { title: "Onboarding", iconSrc: onboardingIcon, key: "onboarding" },
  {
    title: "Digital Wallet",
    iconSrc: digitalWalletIcon,
    key: "digital_wallet",
  },
  { title: "88Pay", iconSrc: eightyEightPayIcon, key: "88pay" },
  { title: "AutoStack", iconSrc: autostackIcon, key: "autostack" },
];

const SEARCH_DEBOUNCE_MS = 400;

export default function SupportFAQ() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [openIndex, setOpenIndex] = useState(0);
  const [categoryId, setCategoryId] = useState(null);
  const [subCategoryId, setSubCategoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const { data: categories = [], isLoading: categoriesLoading } =
    useFaqCategories();
  const { data: faqList = [], isLoading: faqLoading } = useFaq(
    categoryId,
    subCategoryId
  );
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");

  const tabsAgain = useMemo(() => {
    const categoryByKey = new Map((categories || []).map((c) => [c.name, c]));
    return TAB_ORDER.filter((tab) => {
      if (tab.title === "Invest") return featureFlags?.invest;
      return categoryByKey.has(tab.key);
    }).map((tab) => ({
      ...tab,
      _id: categoryByKey.get(tab.key)?._id,
      subCategories: categoryByKey.get(tab.key)?.subCategories || [],
    }));
  }, [categories, featureFlags?.invest]);

  const currentTab = tabsAgain.at(activeTab) || tabsAgain.at(0);
  const { data: searchData, isLoading: searchLoading } = useFaqSearch(
    debouncedSearchQuery,
    { category: currentTab?.key, limit: 50 }
  );
  const searchResults = searchData?.results ?? [];

  useEffect(() => {
    const fromState = location.state?.searchQuery;
    if (typeof fromState === "string" && fromState.trim()) {
      setSearchQuery(fromState.trim());
      setDebouncedSearchQuery(fromState.trim());
    }
  }, [location.state?.searchQuery]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setDebouncedSearchQuery("");
      return;
    }
    const t = setTimeout(
      () => setDebouncedSearchQuery(searchQuery.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const currentFaqs = useMemo(() => {
    if (!Array.isArray(faqList)) return [];
    return faqList.filter((item) => item.complianceApproved !== false);
  }, [faqList]);

  useEffect(() => {
    if (categoriesLoading || !tabsAgain.length) return;
    if (categoryId == null && currentTab?._id) {
      setCategoryId(currentTab._id);
    }
  }, [categoriesLoading, tabsAgain.length, categoryId, currentTab?._id]);

  useEffect(() => {
    setOpenIndex(0);
  }, [activeTab]);

  useEffect(() => {
    setSubCategoryId(null);
  }, [categoryId]);

  const handleTabClick = (index) => {
    const selected = tabsAgain.at(index);
    if (selected?._id) {
      setCategoryId(selected._id);
      setActiveTab(index);
    }
  };

  const handleSubCategoryClick = (subId) => {
    setSubCategoryId((current) => (current === subId ? null : subId));
  };
  return (
    <>
      <Header />
      <div className="min-h-screen w-full bg-gray-100 flex flex-col">
        <div className="h-[72px] bg-gradient-to-r from-[#5F75FF] to-[#9747FF] lg:flex hidden items-center pl-20 text-white ">
          <div className="mr-5 border-rounded rounded-full p-2.5 border">
            <ArrowLeft
              size={18}
              onClick={() => {
                navigate("/support/index");
              }}
            />
          </div>
          <p className="text-[16px] font-semibold">
            Support <span className="mx-2 font-normal">/</span>{" "}
            <span>FAQs</span>
          </p>
        </div>

        <div className="flex justify-center mt-8 px-4 md:px-16 lg:px-32 h-full item">
          <div className="w-full flex flex-row">
            <div className="w-[302px] space-y-[12px] p-[24px] lg:block hidden">
              {tabsAgain.map((tab, index) => (
                <Card
                  key={index}
                  className={`cursor-pointer rounded-full h-[56px] ${
                    activeTab === index
                      ? "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)]"
                      : "bg-transparent border-none !shadow-none"
                  }`}
                  onClick={() => handleTabClick(index)}
                >
                  <CardContent
                    className={`flex items-center gap-[12px] px-[24px] py-[16px] text-[14px] ${
                      activeTab === index
                        ? "text-white font-semibold"
                        : "text-[#6D6584]"
                    }`}
                  >
                    {activeTab === index ? (
                      <span className="inline-flex w-6 h-6 shrink-0 [filter:brightness(0)_invert(1)]">
                        <img
                          className="w-[24px] h-[24px]"
                          src={tab.iconSrc}
                          alt=""
                          aria-hidden
                        />
                      </span>
                    ) : (
                      <span
                        className="inline-block w-6 h-6 shrink-0 bg-[#6D6584] [mask-size:contain] [mask-repeat:no-repeat] [mask-position:center] [-webkit-mask-size:contain] [-webkit-mask-repeat:no-repeat] [-webkit-mask-position:center]"
                        style={{
                          WebkitMaskImage: `url(${tab.iconSrc})`,
                          maskImage: `url(${tab.iconSrc})`,
                        }}
                      />
                    )}
                    {tab.title}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="lg:pl-8 pl-0 flex-1">
              <Card className="bg-white border-[#EBE9EF] rounded-2xl w-full shadow-sm overflow-hidden">
                <div className="flex lg:hidden items-center space-x-4 px-3 pt-4 ">
                  <button
                    type="button"
                    className="w-12 h-12 flex items-center justify-center rounded-full border border-[#F1EDF7] bg-transparent text-[#6D6584]"
                    aria-label="Back"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <p className="font-plusjakarta font-semibold text-[16px] text-[#33125F]">
                    Support
                  </p>
                </div>
                <CardHeader className="px-6 pt-6 pb-4">
                  <CardTitle className="text-[20px] font-semibold lg:text-[#6D6584] text-[#33125F]">
                    FAQ {">"} {currentTab?.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="px-6 pb-4">
                  <div className="flex-1 lg:min-w-[600px] min-w-full relative lg:mb-5 mb-8">
                    <Input
                      type="text"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full lg:h-[56px] h-[40px] pl-12 pr-4 rounded-full text-[#6D6584] text-[14px] border border-[#EBE9EF] hover:border-brand-light focus:border-brand-light focus-visible:border-brand-light active:border-brand-light focus:ring focus:ring-brand-light focus-visible:ring focus-visible:ring-brand-light outline-none focus:outline-none focus-visible:outline-none active:outline-none transition-colors bg-[#EBE9EF4D]"
                    />
                    {searchLoading ? (
                      <Loader2 className="absolute left-[1.5rem] lg:top-[1.3rem] top-[0.9rem] w-[15px] h-[15px] text-[#6D6584] animate-spin" />
                    ) : (
                      <Search className="absolute left-[1.5rem] lg:top-[1.3rem] top-[0.9rem] w-[15px] h-[15px] text-[#6D6584] transition-transform duration-300 ease-in-out hover:scale-110" />
                    )}
                  </div>
                  {(currentTab?.subCategories?.length ?? 0) > 0 && (
                    <div className="flex flex-wrap items-center justify-start gap-[8px] mb-[20px]">
                      <Button
                        variant="outline"
                        onClick={() => setSubCategoryId(null)}
                        className={`rounded-full p-[24px] pb-[26px] text-[14px] font-semibold ${
                          subCategoryId === null
                            ? "bg-[#33125F] text-white"
                            : "text-[#6D6584] hover:bg-[#F1EDF7] hover:text-[#6D6584]"
                        }`}
                      >
                        All
                      </Button>
                      {(currentTab?.subCategories || []).map((sub) => (
                        <Button
                          key={sub._id}
                          variant="outline"
                          onClick={() => handleSubCategoryClick(sub._id)}
                          className={`rounded-full p-[24px] pb-[26px] text-[14px] font-semibold ${
                            subCategoryId === sub._id
                              ? "bg-[#33125F] text-white"
                              : "text-[#6D6584] hover:bg-[#F1EDF7] hover:text-[#6D6584]"
                          }`}
                        >
                          {sub.name}
                        </Button>
                      ))}
                    </div>
                  )}
                  {debouncedSearchQuery.trim() ? (
                    categoriesLoading || searchLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin text-black" />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {searchResults.length > 0 ? (
                          searchResults.map((faq, index) => {
                            const isOpen = openIndex === index;
                            return (
                              <div
                                key={faq.id ?? index}
                                className="border border-[1px] border-[#EBE9EF] rounded-lg px-[24px] py-[24px] bg-white"
                              >
                                <button
                                  className="flex items-center justify-between w-full text-left"
                                  onClick={() =>
                                    setOpenIndex(isOpen ? -1 : index)
                                  }
                                >
                                  <span className="text-[16px] font-semibold text-[#423E51]">
                                    {faq.question}
                                  </span>
                                  <div className="w-[40px] h-[40px] flex items-center justify-center border border-[#EBE9EF] rounded-full">
                                    {isOpen ? (
                                      <span className="text-[24px] text-[#6D6584] -mt-[5px]">
                                        –
                                      </span>
                                    ) : (
                                      <span className="text-[24px] text-[#6D6584] -mt-[5px]">
                                        +
                                      </span>
                                    )}
                                  </div>
                                </button>
                                {isOpen && (
                                  <div className="mt-5 text-[16px] text-[#6D6584]">
                                    {faq.answer}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="py-8 text-center text-[#6D6584] text-[16px]">
                            No results found for &quot;{debouncedSearchQuery}
                            &quot;
                          </div>
                        )}
                      </div>
                    )
                  ) : categoriesLoading || faqLoading ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="animate-spin text-black" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {currentFaqs.map((faq, index) => {
                        const isOpen = openIndex === index;
                        return (
                          <div
                            key={faq._id ?? index}
                            className="border border-[1px] border-[#EBE9EF] rounded-lg px-[24px] py-[24px] bg-white"
                          >
                            <button
                              className="flex items-center justify-between w-full text-left"
                              onClick={() => setOpenIndex(isOpen ? -1 : index)}
                            >
                              <span className="text-[16px] font-semibold text-[#423E51]">
                                {faq.question}
                              </span>
                              <div className="w-[40px] h-[40px] flex items-center justify-center border border-[#EBE9EF] rounded-full">
                                {isOpen ? (
                                  <span className="text-[24px] text-[#6D6584] -mt-[5px]">
                                    –
                                  </span>
                                ) : (
                                  <span className="text-[24px] text-[#6D6584] -mt-[5px]">
                                    +
                                  </span>
                                )}
                              </div>
                            </button>
                            {isOpen && (
                              <div className="mt-5 text-[16px] text-[#6D6584]">
                                {faq.answer}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>

                <Separator />

                <div className="pt-4 lg:pb-6 pb-8 text-center text-[16px] text-[#6D6584]">
                  Questions?{" "}
                  <a
                    href="/support/whatsapp"
                    className="lg:text-[#EB3366] text-[#33125F] font-semibold hover:underline"
                  >
                    Contact us
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
